import { apiClient } from './api.client';

export interface UploadResponse {
  success: boolean;
  data?: {
    url: string;
    publicUrl: string;
    path: string;
  };
  error?: {
    code: string;
    message: string;
  };
}

export interface UploadMultipleResponse {
  success: boolean;
  data?: {
    uploaded: Array<{
      url: string;
      publicUrl: string;
      path: string;
    }>;
    failed?: Array<{
      error: string;
    }>;
  };
  error?: {
    code: string;
    message: string;
  };
}

/**
 * Upload a single image to the server
 */
export async function uploadImage(
  file: File,
  options?: { folder?: string }
): Promise<UploadResponse> {
  try {
    const formData = new FormData();
    formData.append('image', file);

    if (options?.folder) {
      formData.append('folder', options.folder);
    }

    const response = await apiClient.post('upload/image', {
      body: formData,
    });

    return await response.json<UploadResponse>();
  } catch (error: unknown) {
    let errorData = {
      code: 'UPLOAD_ERROR',
      message: 'Failed to upload image',
    };

    if (error && typeof error === 'object' && 'response' in error) {
      // Handle Ky HTTPError
      const httpError = error as { response: Response };
      try {
        const body = await httpError.response.json<{
          error: { code: string; message: string };
        }>();
        if (body.error) {
          errorData = body.error;
        }
      } catch (e) {
        // Could not parse error response, use default
      }
    } else if (error instanceof Error) {
      errorData.message = error.message;
    }

    return {
      success: false,
      error: errorData,
    };
  }
}

/**
 * Upload multiple images to the server
 */
export async function uploadMultipleImages(
  files: File[],
  options?: { folder?: string }
): Promise<UploadMultipleResponse> {
  try {
    const formData = new FormData();

    files.forEach((file) => {
      formData.append('images', file);
    });

    if (options?.folder) {
      formData.append('folder', options.folder);
    }

    const response = await apiClient.post('upload/images', {
      body: formData,
    });

    return await response.json<UploadMultipleResponse>();
  } catch (error: unknown) {
    let errorData = {
      code: 'UPLOAD_ERROR',
      message: 'Failed to upload images',
    };

    if (error && typeof error === 'object' && 'response' in error) {
      // Handle Ky HTTPError
      const httpError = error as { response: Response };
      try {
        const body = await httpError.response.json<{
          error: { code: string; message: string };
        }>();
        if (body.error) {
          errorData = body.error;
        }
      } catch (e) {
        // Could not parse error response, use default
      }
    } else if (error instanceof Error) {
      errorData.message = error.message;
    }

    return {
      success: false,
      error: errorData,
    };
  }
}

/**
 * Upload a single video file to the server
 */
export async function uploadVideo(
  file: File,
  options?: { folder?: string }
): Promise<UploadResponse> {
  try {
    const formData = new FormData();
    formData.append('video', file);

    if (options?.folder) {
      formData.append('folder', options.folder);
    }

    const response = await apiClient.post('upload/video', {
      body: formData,
      timeout: 300000, // 5 minutes timeout for large video files
    });

    return await response.json<UploadResponse>();
  } catch (error: unknown) {
    let errorData = {
      code: 'UPLOAD_ERROR',
      message: 'Failed to upload video',
    };

    if (error && typeof error === 'object' && 'response' in error) {
      const httpError = error as { response: Response };
      try {
        const body = await httpError.response.json<{
          error: { code: string; message: string };
        }>();
        if (body.error) {
          errorData = body.error;
        }
      } catch {
        // Could not parse error response
      }
    } else if (error instanceof Error) {
      errorData.message = error.message;
    }

    return {
      success: false,
      error: errorData,
    };
  }
}

/**
 * Delete an image from the server
 */
export async function deleteImage(path: string): Promise<boolean> {
  try {
    await apiClient.delete('upload/image', {
      json: { path },
    });
    return true;
  } catch (error) {
    console.error('Failed to delete image:', error);
    return false;
  }
}
